import {
  useState,
  MouseEvent,
  FC,
  useEffect,
  ChangeEvent,
} from 'react';

// Third party
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js';
import Box from '@mui/material/Box';
import { useController, useFormContext } from 'react-hook-form';
import Icon from '@mdi/react';

// Hooks
import { useTranslation } from 'react-i18next';
import { BaseLabel } from '@mgm/common/components/base';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';

// Types
import { TPasswordInput } from './type';

// Redux-toolkit
import { getPasswordValidation } from '../../store/common/selectors';
import { commonActions } from '../../store/common/slice';

// Components

const PasswordField: FC<TPasswordInput> = ({
  rules,
  isCheckValidation = false,
  placeholder,
  labelText,
  name = 'password',
  onChange,
  isDisplayLabel = true,
  size = 'small',
  isRequired = false,
}) => {
  const passwordValidation = useAppSelector(getPasswordValidation);
  const dispatch = useAppDispatch();
  const { t } = useTranslation('inputs');

  const { control } = useFormContext();

  const {
    field: { ref, ...inputProps },
    fieldState: { invalid },
  } = useController({
    name: name || '',
    control,
    rules: {
      required: isRequired,
      validate: rules,
    },
    defaultValue: '',
  });

  const initPasswordValidation = () => {
    Object.keys(passwordValidation).forEach((key) => {
      dispatch(commonActions.setPasswordValidation({
        key,
        value: null,
      }));
    });
  };

  useEffect(() => {
    if (invalid) {
      initPasswordValidation();
    }
  }, [invalid, isCheckValidation]);

  const [isShowPassword, setIsShowPassword] = useState(false);
  const handleClickShowPassword = () => setIsShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    inputProps.onChange(e);

    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <Box>
      {isDisplayLabel && <BaseLabel letterSpacing="-0.5px">{labelText || t('password')}</BaseLabel>}
      <OutlinedInput
        {...inputProps}
        inputRef={ref}
        id={name}
        onChange={handleChange}
        type={isShowPassword ? 'text' : 'password'}
        {...(name === 'password' && {
          autoComplete: 'current-password',
        })}
        size={size}
        placeholder={placeholder || t('enterPassword')}
        fullWidth
        endAdornment={(
          <InputAdornment position="end">
            <IconButton
              disableRipple
              color="primary"
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {isShowPassword
                ? <Icon path={mdiEyeOutline} size={0.7} />
                : <Icon path={mdiEyeOffOutline} size={0.7} /> }
            </IconButton>
          </InputAdornment>
      )}
      />
    </Box>
  );
};

export {
  PasswordField,
};
